import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast, Toaster } from 'sonner';
import { Star, Crown, Shield, BarChart3, Settings2, Info, Volume2, TrendingUp, Trophy, Target, Clock, Lightbulb, RotateCcw, MessageCircle, Send, Lock, Sparkles } from 'lucide-react';
import { getState, setState, resetProgress, deleteProfile, getMilestones } from '../core/storage';
import { MILESTONE, recentMilestones } from '../core/milestones';
import { renderShareCard, loadPiko, shareCardBlob } from '../core/shareCard';
import { audio } from '../core/audio';
import { t } from '../core/i18n';
import { useEntitlement } from '../core/entitlement';
import { api, formatApiErrorDetail } from '../core/api';
import { getAffiliateAttributionToken } from '../core/referralAttribution';
import { parentGatePathForTab } from '../core/parentNavigation';
import ParentAffiliatePanel from './affiliate/ParentAffiliatePanel';
import { MISSIONS } from '../content/missions';
import { GAMES, ACTIVE_GAMES, WORLDS, worldGames, proGameCount, proWorldsWithGames, gameName, SKILL_LABELS, canPlayGame } from '../content/games';

function SkillRow({ skill, data, en }) {
  const ratio = data.attempts ? data.correct / data.attempts : 0;
  const label = SKILL_LABELS[skill]?.[en ? 1 : 0] || skill;
  const text = data.attempts < 3 ? (en ? 'Just getting started' : 'Baru bermula')
    : ratio >= 0.7 ? (en ? 'Recognised consistently' : 'Dikenal pasti secara konsisten')
    : (en ? 'Still practising' : 'Masih berlatih');
  return (
    <div className="flex items-center justify-between py-3 border-b border-navy/5 last:border-0" data-testid={`skill-${skill}`}>
      <span className="font-body font-semibold text-navy">{label}</span>
      <span className={`font-body text-sm px-3 py-1 rounded-full ${ratio >= 0.7 && data.attempts >= 3 ? 'bg-leaf/20 text-green-800' : 'bg-sunshine/25 text-navy/70'}`}>{text}</span>
    </div>
  );
}

// Development-only. Never true in a production build, so the whole panel and
// its tab drop out of the app the customer installs.
const SHOW_VOICE_REVIEW = process.env.NODE_ENV === 'development';

function VoiceReview({ en }) {
  const [candidates, setCandidates] = useState(null);
  const [approved, setApproved] = useState(() => localStorage.getItem('pp_voice_choice') || '');

  useEffect(() => {
    fetch('/assets/voice/candidates/manifest.json')
      .then((r) => (r.ok ? r.json() : null))
      .then(setCandidates)
      .catch(() => setCandidates(null));
  }, []);

  const play = (name, lng, i) => {
    const a = new Audio(`/assets/voice/candidates/${name}/${lng}/sample${i}.mp3`);
    a.play().catch(() => toast.error(en ? 'Sample not available' : 'Sampel tidak tersedia'));
  };

  return (
    <div className="mt-6 space-y-5" data-testid="voice-review">
      <div className="bg-white/90 rounded-3xl p-6 shadow-sm">
        <h2 className="font-heading text-lg font-bold text-navy">{en ? 'Voice Review (parent / development)' : 'Semakan Suara (ibu bapa / pembangunan)'}</h2>
        <p className="font-body text-sm text-navy/70 mt-2">
          {en ? 'Current app voice: OpenAI TTS (temporary). ElevenLabs candidate voices will appear here once generated — the account quota must be available first.'
            : 'Suara aplikasi semasa: OpenAI TTS (sementara). Calon suara ElevenLabs akan dipaparkan di sini selepas dijana — kuota akaun perlu tersedia dahulu.'}
        </p>
        {!candidates ? (
          <p className="font-body text-sm text-coral mt-3" data-testid="voice-candidates-missing">
            {en ? 'Candidate samples not yet generated (ElevenLabs quota exhausted: 39,999/40,000 characters used). Top up the ElevenLabs account, then ask the builder to run the candidate generation.'
              : 'Sampel calon belum dijana (kuota ElevenLabs habis: 39,999/40,000 aksara digunakan). Tambah nilai akaun ElevenLabs, kemudian minta pembina menjana calon suara.'}
          </p>
        ) : (
          <div className="space-y-4 mt-4">
            {candidates.map((c) => (
              <div key={c.name} className="border-2 border-navy/10 rounded-2xl p-4" data-testid={`voice-candidate-${c.name}`}>
                <div className="flex items-center justify-between">
                  <p className="font-heading font-bold text-navy capitalize">{c.name} <span className="font-body text-xs text-navy/40">({c.model})</span></p>
                  <button data-testid={`approve-voice-${c.name}`}
                    onClick={() => { localStorage.setItem('pp_voice_choice', c.name); setApproved(c.name); toast.success('OK'); }}
                    className={`px-4 py-1.5 rounded-full font-body text-sm font-bold ${approved === c.name ? 'bg-leaf text-navy' : 'bg-cream text-navy/60'}`}>
                    {approved === c.name ? (en ? 'Approved ✓' : 'Diluluskan ✓') : (en ? 'Approve' : 'Luluskan')}
                  </button>
                </div>
                {['ms', 'en'].map((lng) => (
                  <div key={lng} className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className="font-body text-xs font-bold text-navy/50 w-8 uppercase">{lng}</span>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <button key={i} onClick={() => play(c.name, lng, i)}
                        className="w-9 h-9 rounded-full bg-sky/15 text-sky font-body text-xs font-bold">{i}</button>
                    ))}
                  </div>
                ))}
              </div>
            ))}
            <p className="font-body text-xs text-navy/40">{en ? 'After approving, the full Malay & English library will be regenerated with the chosen voice during development. No API key is ever exposed in the app.' : 'Selepas diluluskan, perpustakaan penuh Melayu & Inggeris akan dijana semula dengan suara pilihan semasa pembangunan. Tiada kunci API didedahkan dalam aplikasi.'}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ParentDashboard() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user, entitlement, transactions, refresh, login, register, logout } = useEntitlement();
  const isPro = entitlement?.plan === 'PRO';
  // ?tab=voice must not open a tab that no longer exists in production, or the
  // panel renders empty with no way back.
  const [tab, setTab] = useState(() => {
    const req = params.get('tab');
    if (req === 'voice' && !SHOW_VOICE_REVIEW) return 'reports';
    const valid = ['reports', 'settings', 'upgrade', 'about', 'affiliate'];
    return valid.includes(req) ? req : 'reports';
  });
  const [st, setSt] = useState(getState());
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [authMode, setAuthMode] = useState('login');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [waSending, setWaSending] = useState(false);
  const [waLastSent, setWaLastSent] = useState(null);
  const [waConsent, setWaConsent] = useState(false);
  const [registerWaConsent, setRegisterWaConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem('pp_parent_gate') !== '1') {
      nav(parentGatePathForTab(tab), { replace: true });
    }
  }, [nav, tab]);

  // A Free parent is NOT bounced off the affiliate tab any more. They see the
  // programme locked, the same way the weekly WhatsApp report is shown locked —
  // a parent who cannot see it cannot want it.

  const en = st.profile.language === 'en';
  const upd = (patch) => setSt(setState(patch));
  const updSettings = (p) => { upd({ settings: { ...st.settings, ...p } }); audio.refreshMusic(); };
  const updProfile = (p) => upd({ profile: { ...st.profile, ...p } });

  const suggested = useMemo(() => MISSIONS.slice().sort(() => Math.random() - 0.5).slice(0, 3), []);

  const childName = st.profile.nickname || (en ? 'Your child' : 'Anak anda');

  // Share card. The parent must see the exact image before any share sheet
  // opens — this leaves the device and lands in a family group chat.
  const [cardUrl, setCardUrl] = useState(null);
  const [cardBlob, setCardBlob] = useState(null);
  const [cardBusy, setCardBusy] = useState(false);

  useEffect(() => () => { if (cardUrl) URL.revokeObjectURL(cardUrl); }, [cardUrl]);

  const makeCard = async () => {
    setCardBusy(true);
    try {
      const piko = await loadPiko();
      const weekMoments = recentMilestones(7).map((m) => milestoneLines.find((l) => l.key === m.key)?.text).filter(Boolean);
      const weekStars = (st.sessions || [])
        .filter((s) => Date.now() - new Date(s.date).getTime() <= 7 * 86400000)
        .reduce((a, s) => a + (s.stars || 0), 0);
      const blob = await renderShareCard({
        name: childName, moments: weekMoments, stars: weekStars,
        topSkill: report.strong?.[0] ? (SKILL_LABELS[report.strong[0]]?.[en ? 1 : 0] || report.strong[0]) : null,
        en, piko,
      });
      if (cardUrl) URL.revokeObjectURL(cardUrl);
      setCardBlob(blob);
      setCardUrl(blob ? URL.createObjectURL(blob) : null);
    } finally {
      setCardBusy(false);
    }
  };

  const sendCard = async () => {
    const how = await shareCardBlob(cardBlob, `pintarplay-${childName}.png`, en ? `${childName}'s week` : `Minggu ${childName}`);
    if (how === 'downloaded') toast.success(en ? 'Picture saved' : 'Gambar disimpan');
  };

  // Growth evidence: is the child needing fewer tries than they used to?
  // Compares the oldest quarter of rounds against the most recent quarter.
  // Never extrapolates — under two weeks of history it says so plainly rather
  // than drawing a line the data cannot support.
  const growth = useMemo(() => {
    const sessions = (st.sessions || []).filter((s) => s?.date && !Number.isNaN(new Date(s.date).getTime()));
    if (sessions.length < 2) return { enough: false, spanDays: 0 };
    const times = sessions.map((s) => new Date(s.date).getTime());
    const spanDays = Math.floor((Math.max(...times) - Math.min(...times)) / 86400000);
    // Oldest first, so "early" really is early.
    const acts = sessions
      .slice()
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .flatMap((s) => (s.activities || []).filter((a) => typeof a.attempts === 'number' && a.attempts > 0));
    if (spanDays < 14 || acts.length < 12) return { enough: false, spanDays };
    const q = Math.max(3, Math.floor(acts.length / 4));
    const mean = (arr) => arr.reduce((a, x) => a + x.attempts, 0) / arr.length;
    const before = mean(acts.slice(0, q));
    const now = mean(acts.slice(-q));
    const weeks = Math.max(2, Math.round(spanDays / 7));
    return { enough: true, spanDays, weeks, before, now, improved: now < before - 0.15 };
  }, [st.sessions]);

  // Turn stored milestones into plain sentences, newest first. Anything whose
  // game or world no longer exists is skipped rather than rendered half-empty.
  const milestoneLines = useMemo(() => {
    const fmtDate = (iso) => {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleDateString(en ? 'en-GB' : 'ms-MY', { day: 'numeric', month: 'short' });
    };
    return getMilestones().map((m) => {
      const g = m.gameId ? GAMES.find((x) => x.id === m.gameId) : null;
      const gName = g ? gameName(g, en) : null;
      const wName = m.worldId && WORLDS[m.worldId] ? t(WORLDS[m.worldId].nameKey) : null;
      const sName = m.skill ? (SKILL_LABELS[m.skill]?.[en ? 1 : 0] || m.skill) : null;
      let text = null;
      if (m.type === MILESTONE.FIRST_GAME && gName) text = t('mFirstGame').replace('{game}', gName);
      else if (m.type === MILESTONE.FIRST_PERFECT && gName) text = t('mFirstPerfect').replace('{game}', gName);
      else if (m.type === MILESTONE.FIRST_SKILL && sName) text = t('mFirstSkill').replace('{skill}', sName);
      else if (m.type === MILESTONE.FIRST_WORLD && wName) text = t('mFirstWorld').replace('{world}', wName);
      if (!text) return null;
      return { key: m.key, text: text.replace('{name}', childName), when: fmtDate(m.date) };
    }).filter(Boolean);
  }, [st.milestones, en, childName]); // eslint-disable-line

  const gameStats = st.gameStats || {};
  const [trendRange, setTrendRange] = useState(7);

  // Real gameplay analytics only — no synthetic scores, no comparisons.
  const report = useMemo(() => {
    const acts = st.sessions.flatMap((s) => s.activities || []);
    const rounds = acts.length;
    const firstTry = acts.filter((a) => a.correct && a.attempts === 1).length;
    const accuracy = rounds ? Math.round((firstTry / rounds) * 100) : null;
    const durs = acts.map((a) => a.durationMs).filter((d) => typeof d === 'number' && d > 0 && d < 120000);
    const avgSec = durs.length ? durs.reduce((a, b) => a + b, 0) / durs.length / 1000 : null;
    const hints = acts.reduce((a, x) => a + (x.hints || 0), 0);
    const retries = acts.reduce((a, x) => a + Math.max(0, (x.attempts || 1) - 1), 0);
    const completed = Object.values(gameStats).filter((g) => g.completed).length;
    const strong = [], practice = [];
    Object.entries(st.skills || {}).forEach(([k, v]) => {
      if (v.attempts >= 4) {
        const r = v.correct / v.attempts;
        if (r >= 0.75) strong.push(k); else if (r < 0.55) practice.push(k);
      }
    });
    const days = (n) => {
      const out = [];
      for (let i = n - 1; i >= 0; i--) {
        const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - i);
        out.push({ key: d.toDateString(), label: `${d.getDate()}/${d.getMonth() + 1}`, stars: 0, rounds: 0, firstTry: 0 });
      }
      st.sessions.forEach((s) => {
        const b = out.find((x) => x.key === new Date(s.date).toDateString());
        if (b) {
          b.stars += s.stars || 0;
          (s.activities || []).forEach((a) => { b.rounds += 1; if (a.correct && a.attempts === 1) b.firstTry += 1; });
        }
      });
      return out;
    };
    return { rounds, accuracy, avgSec, hints, retries, completed, strong, practice, trend7: days(7), trend30: days(30) };
  }, [st]); // eslint-disable-line

  const recommended = useMemo(() => {
    const playable = ACTIVE_GAMES.filter((g) => canPlayGame(entitlement, g.id, st.progress.stars));
    const notDone = playable.filter((g) => !gameStats[g.id]?.completed);
    return notDone.find((g) => report.practice.includes(g.skill))
      || playable.find((g) => report.practice.includes(g.skill))
      || notDone[0]
      || playable.slice().sort((a, b) => (gameStats[a.id]?.plays || 0) - (gameStats[b.id]?.plays || 0))[0];
  }, [entitlement, st, report]); // eslint-disable-line

  const skillLevels = useMemo(() => {
    const out = {};
    ACTIVE_GAMES.forEach((g) => {
      const s = gameStats[g.id];
      if (!s?.plays) return;
      out[g.skill] = Math.max(out[g.skill] || 1, s.level);
    });
    return out;
  }, []); // eslint-disable-line

  const levelPhrase = (skill, lvl) => {
    const lbl = (SKILL_LABELS[skill]?.[en ? 1 : 0] || skill).toLowerCase();
    if (lvl >= 3) return en ? `Very confident with ${lbl}` : `Sangat yakin dengan ${lbl}`;
    if (lvl === 2) return en ? `Growing confident with ${lbl}` : `Semakin yakin dengan ${lbl}`;
    return en ? `Still practising ${lbl}` : `Masih berlatih ${lbl}`;
  };

  const doAuth = async () => {
    setBusy(true);
    try {
      await (authMode === 'login'
        ? login(phone, password)
        : register(phone, password, registerWaConsent));
      toast.success(en ? 'Signed in' : 'Berjaya log masuk');
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };

  const sendOtp = async () => {
    setBusy(true);
    try {
      await api.post('/auth/forgot-password', { phone });
      setOtpSent(true);
      toast.success(en ? 'OTP sent to your WhatsApp' : 'OTP dihantar ke WhatsApp anda');
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };

  const resetPw = async () => {
    setBusy(true);
    try {
      await api.post('/auth/reset-password', { phone, otp, new_password: newPassword });
      toast.success(en ? 'Password updated! Please log in.' : 'Kata laluan dikemas kini! Sila log masuk.');
      setAuthMode('login'); setOtpSent(false); setOtp(''); setNewPassword(''); setPassword('');
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };

  // The pay buttons stay live for a Free parent. Greying them out told someone
  // who had already decided to buy that they were not allowed to, which is the
  // worst place in the product to put a wall. Pressing one now carries them to
  // the account form instead of doing nothing.
  const requireAccount = () => {
    if (user) return true;
    document.querySelector('[data-testid="parent-auth-card"]')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => document.querySelector('[data-testid="auth-phone-input"]')?.focus(), 400);
    toast.info(en ? 'Log in or sign up to continue.' : 'Log masuk atau daftar untuk teruskan.');
    return false;
  };

  const payStripe = async (plan = 'monthly') => {
    if (!requireAccount()) return;
    setBusy(true);
    try {
      const r = await api.post('/billing/stripe/create-checkout-session', {
        origin_url: window.location.origin,
        plan,
        attributionToken: getAffiliateAttributionToken(),
      });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  const payFpx = async (plan = 'monthly') => {
    if (!requireAccount()) return;
    setBusy(true);
    try {
      const r = await api.post('/billing/billplz/create-bill', {
        origin_url: window.location.origin,
        plan,
        attributionToken: getAffiliateAttributionToken(),
      });
      localStorage.setItem('pp_last_bill', r.data.bill_id);
      window.location.href = r.data.bill_url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };

  const cancelStripe = async () => {
    setBusy(true);
    try { await api.post('/billing/stripe/cancel'); await refresh(); toast.success(en ? 'Auto-renewal cancelled. Access continues until period end.' : 'Pembaharuan automatik dibatalkan. Akses kekal hingga akhir tempoh.'); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  // The Voice tab is a build-time review tool: it lists unapproved ElevenLabs
  // candidate samples so the voice can be chosen during development. It is not
  // a customer feature — a paying parent has nothing to do with it — so it is
  // kept out of the shipped app entirely rather than hidden behind Pro.
  const TABS = [
    ['reports', en ? 'Reports' : 'Laporan', BarChart3],
    ['settings', en ? 'Settings' : 'Tetapan', Settings2],
    ['upgrade', 'Pro', Crown],
    // Always present. A Free parent sees it locked rather than not at all —
    // hiding the earner from the people who have not paid yet sells nothing.
    ['affiliate', t('affiliate'), TrendingUp],
    ...(SHOW_VOICE_REVIEW ? [['voice', en ? 'Voice' : 'Suara', Volume2]] : []),
    ['about', en ? 'About' : 'Info', Info],
  ];

  const waData = useMemo(() => ({
    nickname: st.profile.nickname || 'Si Manja',
    gamesCompleted: report.completed,
    totalGames: ACTIVE_GAMES.length,
    stars: st.progress.starsEarned ?? st.progress.stars,
    accuracy: report.accuracy,
    topSkill: report.strong.length ? (SKILL_LABELS[report.strong[0]]?.[0] || report.strong[0]) : null,
    practiceSkill: report.practice.length ? (SKILL_LABELS[report.practice[0]]?.[0] || report.practice[0]) : null,
    recommendedGame: recommended ? recommended.name[0] : null,
    starsThisWeek: report.trend7.reduce((a, d) => a + d.stars, 0),
    roundsThisWeek: report.trend7.reduce((a, d) => a + d.rounds, 0),
  }), [report, recommended, st]);

  const waMessage = useMemo(() => {
    const d = waData;
    const lines = ['ðŸ¦• *Laporan Piko Minggu Ini*', '', `Hai! Piko nak share sikit progress ${d.nickname} minggu ni ðŸ˜„`, ''];
    lines.push(`â­ Bintang minggu ni: ${d.starsThisWeek} (jumlah semua: ${d.stars})`);
    lines.push(`ðŸŽ® Game selesai: ${d.gamesCompleted}/${d.totalGames}`);
    if (d.roundsThisWeek) lines.push(`ðŸ”„ Pusingan dimainkan minggu ni: ${d.roundsThisWeek}`);
    if (d.accuracy != null) lines.push(`ðŸŽ¯ Ketepatan cubaan pertama: ${d.accuracy}%`);
    if (d.topSkill) lines.push(`ðŸ’ª Paling power: ${d.topSkill}`);
    if (d.practiceSkill) lines.push(`ðŸ“š Boleh latih lagi: ${d.practiceSkill}`);
    lines.push('');
    if (d.recommendedGame) lines.push(`Minggu depan Piko cadangkan cuba *${d.recommendedGame}* — confirm ${d.nickname} suka punya!`);
    lines.push('Terus main, terus pandai. Jumpa lagi minggu depan ya! ðŸŒŸ');
    lines.push('— Piko, PintarPlay');
    return lines.join('\n');
  }, [waData]);

  useEffect(() => {
    if (!user || !isPro) return;
    api.post('/reports/sync', waData).catch(() => {});
    api.get('/reports/whatsapp-status').then((r) => {
      setWaLastSent(r.data.lastSentAt);
      setWaConsent(Boolean(r.data.consent));
    }).catch(() => {});
  }, [user, isPro]); // eslint-disable-line

  const sendWa = async () => {
    setWaSending(true);
    try {
      await api.post('/reports/sync', waData);
      const r = await api.post('/reports/send-whatsapp');
      if (r.data.accepted) {
        setWaLastSent(new Date().toISOString());
        toast.success(en ? `Report sent to ${r.data.sentTo} on WhatsApp!` : `Laporan dihantar ke ${r.data.sentTo} melalui WhatsApp!`);
      } else {
        toast.info(t('weeklyWaAlready'));
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    setWaSending(false);
  };

  const toggleWaConsent = async () => {
    setWaSending(true);
    try {
      const enabled = !waConsent;
      await api.put('/reports/whatsapp-consent', { enabled });
      setWaConsent(enabled);
      toast.success(t(enabled ? 'weeklyWaEnabled' : 'weeklyWaDisabled'));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    setWaSending(false);
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg,#FCF9F2,#F2ECE1)' }} data-testid="parent-dashboard">
      <Toaster richColors position="top-center" />
      <div className="max-w-3xl mx-auto px-5 py-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-navy tracking-tight">PintarPlay</h1>
            <p className="font-body text-navy/60 text-sm">{en ? 'Parent Mode' : 'Mod Ibu Bapa'} · {isPro ? 'Pro' : en ? 'Free' : 'Percuma'}</p>
          </div>
          <button data-testid="parent-back-home-btn" onClick={() => nav('/home')}
            className="font-body font-bold text-navy bg-white rounded-full px-5 py-2.5 shadow min-h-[44px]">{t('backHome')}</button>
        </div>

        <div className="flex gap-2 mt-6 overflow-x-auto pb-1">
          {TABS.map(([id, label, Icon]) => (
            <button key={id} data-testid={`parent-tab-${id}`} onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-body font-bold whitespace-nowrap min-h-[44px] ${tab === id ? 'bg-navy text-white' : 'bg-white text-navy/60'}`}>
              <Icon size={17} /> {label}
            </button>
          ))}
        </div>

        {tab === 'reports' && (
          <div className="grid md:grid-cols-2 gap-5 mt-6">
            {/* Summary from real play data */}
            <div className="bg-white/90 backdrop-blur rounded-3xl p-6 shadow-sm md:col-span-2" data-testid="report-summary">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-navy">{en ? 'Play Summary' : 'Ringkasan Permainan'}</h2>
                <span className="font-body text-[11px] text-navy/40">{en ? 'Real play data only · updates after every game' : 'Data permainan sebenar sahaja · dikemas kini selepas setiap permainan'}</span>
              </div>
              {report.rounds === 0 ? (
                <p className="font-body text-navy/50 text-sm mt-3">{en ? 'Play a game to see the report.' : 'Main satu permainan untuk melihat laporan.'}</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
                  <div className="bg-cream rounded-2xl px-4 py-3" data-testid="stat-completed">
                    <div className="flex items-center gap-1.5 text-navy/50"><Trophy size={14} /><span className="font-body text-[11px] font-bold">{en ? 'Games completed' : 'Game selesai'}</span></div>
                    <p className="font-heading text-2xl font-bold text-navy mt-1">{report.completed}<span className="font-body text-xs text-navy/40">/{ACTIVE_GAMES.length}</span></p>
                  </div>
                  <div className="bg-cream rounded-2xl px-4 py-3" data-testid="stat-stars">
                    <div className="flex items-center gap-1.5 text-navy/50"><Star size={14} /><span className="font-body text-[11px] font-bold">{en ? 'Stars collected' : 'Bintang dikumpul'}</span></div>
                    <p className="font-heading text-2xl font-bold text-navy mt-1">{st.progress.starsEarned ?? st.progress.stars}</p>
                  </div>
                  <div className="bg-cream rounded-2xl px-4 py-3" data-testid="stat-accuracy">
                    <div className="flex items-center gap-1.5 text-navy/50"><Target size={14} /><span className="font-body text-[11px] font-bold">{en ? 'First-try accuracy' : 'Ketepatan cubaan pertama'}</span></div>
                    <p className="font-heading text-2xl font-bold text-navy mt-1">{report.accuracy}%</p>
                  </div>
                  <div className="bg-cream rounded-2xl px-4 py-3" data-testid="stat-response">
                    <div className="flex items-center gap-1.5 text-navy/50"><Clock size={14} /><span className="font-body text-[11px] font-bold">{en ? 'Avg answer time' : 'Purata masa jawab'}</span></div>
                    <p className="font-heading text-2xl font-bold text-navy mt-1">{report.avgSec != null ? `${report.avgSec.toFixed(1)}s` : '—'}</p>
                  </div>
                  <div className="bg-cream rounded-2xl px-4 py-3" data-testid="stat-hints">
                    <div className="flex items-center gap-1.5 text-navy/50"><Lightbulb size={14} /><span className="font-body text-[11px] font-bold">{en ? 'Hints used' : 'Bantuan digunakan'}</span></div>
                    <p className="font-heading text-2xl font-bold text-navy mt-1">{report.hints}</p>
                  </div>
                  <div className="bg-cream rounded-2xl px-4 py-3" data-testid="stat-retries">
                    <div className="flex items-center gap-1.5 text-navy/50"><RotateCcw size={14} /><span className="font-body text-[11px] font-bold">{en ? 'Retries' : 'Cubaan semula'}</span></div>
                    <p className="font-heading text-2xl font-bold text-navy mt-1">{report.retries}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Weekly WhatsApp report */}
            <div className="bg-white/90 backdrop-blur rounded-3xl p-6 shadow-sm md:col-span-2" data-testid="whatsapp-report-card">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="font-heading text-lg font-bold text-navy flex items-center gap-2">
                  <MessageCircle size={19} className="text-leaf" />{en ? 'Weekly WhatsApp Report' : 'Laporan Mingguan WhatsApp'}
                </h2>
                {isPro ? (
                  <span className="font-body text-[11px] font-bold text-green-800 bg-leaf/20 rounded-full px-3 py-1">{en ? 'Auto-sent every week' : 'Dihantar auto setiap minggu'}</span>
                ) : (
                  <span className="font-body text-[11px] font-bold text-navy/60 bg-sunshine/30 rounded-full px-3 py-1 flex items-center gap-1"><Crown size={12} />Pro</span>
                )}
              </div>
              <p className="font-body text-sm text-navy/60 mt-1.5">
                {en ? "A friendly progress summary from Piko, sent straight to your WhatsApp every week."
                  : 'Ringkasan progress mesra daripada Piko, terus ke WhatsApp anda setiap minggu.'}
              </p>
              <div className="relative mt-4">
                <div className="rounded-2xl p-4" style={{ background: '#E7F6E7', border: '1px solid rgba(37,90,60,0.15)' }}>
                  <div className="max-w-md rounded-2xl rounded-tl-sm bg-white shadow-sm px-4 py-3" data-testid="whatsapp-preview-bubble">
                    <p className="font-body text-[13px] text-navy/85 whitespace-pre-line leading-relaxed">{waMessage}</p>
                    <p className="font-body text-[10px] text-navy/35 text-right mt-1.5">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                </div>
                {!isPro && (
                  <div className="absolute inset-0 rounded-2xl flex flex-col items-center justify-center gap-3 backdrop-blur-[3px]" style={{ background: 'rgba(252,249,242,0.55)' }} data-testid="whatsapp-lock-overlay">
                    <div className="w-12 h-12 rounded-full bg-navy flex items-center justify-center"><Lock size={20} className="text-sunshine" /></div>
                    <p className="font-body text-sm font-bold text-navy text-center px-6">{en ? 'Pro parents get this report on WhatsApp automatically, every week.' : 'Ibu bapa Pro terima laporan ini di WhatsApp secara automatik, setiap minggu.'}</p>
                    <button data-testid="whatsapp-upgrade-btn" onClick={() => setTab('upgrade')}
                      className="font-body font-bold text-navy bg-sunshine rounded-full px-6 py-2.5 min-h-[44px] shadow">{en ? 'Upgrade to Pro' : 'Naik taraf ke Pro'}</button>
                  </div>
                )}
              </div>
              {isPro && (
                <div className="flex items-center gap-3 mt-4 flex-wrap">
                  <button data-testid="whatsapp-consent-toggle" disabled={waSending || !user} onClick={toggleWaConsent}
                    className={`font-body font-bold rounded-full px-6 py-2.5 min-h-[44px] disabled:opacity-50 ${waConsent ? 'bg-cream text-coral' : 'bg-leaf text-navy'}`}>
                    {t(waConsent ? 'weeklyWaDisable' : 'weeklyWaEnable')}
                  </button>
                  <button data-testid="whatsapp-send-now-btn" disabled={waSending || !user || !waConsent} onClick={sendWa}
                    className="flex items-center gap-2 font-body font-bold text-white bg-navy rounded-full px-6 py-2.5 min-h-[44px] disabled:opacity-50">
                    <Send size={15} />{waSending ? (en ? 'Sending…' : 'Menghantar…') : (en ? 'Send Now' : 'Hantar Sekarang')}
                  </button>
                  {user?.phone && <span className="font-body text-xs text-navy/50">{en ? 'To' : 'Ke'}: {user.phone}</span>}
                  {waLastSent && <span className="font-body text-xs text-navy/40" data-testid="whatsapp-last-sent">{en ? 'Last sent' : 'Terakhir dihantar'}: {new Date(waLastSent).toLocaleString()}</span>}
                  {!user && <span className="font-body text-xs text-coral">{en ? 'Log in at the Pro tab first.' : 'Log masuk di tab Pro dahulu.'}</span>}
                </div>
              )}
            </div>

            {/* What your child learns — world objectives (Phase 2b) */}
            <div className="bg-white/90 backdrop-blur rounded-3xl p-6 shadow-sm md:col-span-2" data-testid="world-objectives-card">
              <h2 className="font-heading text-lg font-bold text-navy flex items-center gap-2">
                <Lightbulb size={19} className="text-sunshine" />{en ? 'What your child learns in each world' : 'Apa anak belajar di setiap dunia'}
              </h2>
              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                {Object.values(WORLDS).map((w) => {
                  const gids = worldGames(w.id);
                  const done = gids.filter((gid) => (st.gameStats || {})[gid]?.completed).length;
                  return (
                    <div key={w.id} className="rounded-2xl p-4 bg-cream/70" data-testid={`objective-row-${w.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-heading text-sm font-bold text-navy">{t(w.nameKey)}</p>
                        {gids.length === 0
                          ? <span className="font-body text-[10px] font-bold text-navy/45 bg-navy/10 rounded-full px-2.5 py-0.5">{en ? 'Coming soon' : 'Akan datang'}</span>
                          : <span className="font-body text-[10px] font-bold text-navy/55 bg-white rounded-full px-2.5 py-0.5">{done}/{gids.length}</span>}
                      </div>
                      <p className="font-body text-xs text-navy/65 mt-1.5">{w.objective[en ? 'en' : 'ms']}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Share card. Preview is mandatory — the parent sees the exact
                image before any share sheet can open. Free accounts included:
                a locked share button spreads nothing. */}
            <div className="bg-white/90 backdrop-blur rounded-3xl p-6 shadow-sm" data-testid="share-panel">
              <div className="flex items-center gap-2">
                <Send size={18} strokeWidth={2.6} className="text-sky" />
                <h2 className="font-heading text-lg font-bold text-navy">{t('shareTitle')}</h2>
              </div>
              <p className="font-body text-navy/55 text-xs mt-2 leading-relaxed" data-testid="share-disclosure">
                {t('shareDesc').replace('{name}', childName)}
              </p>

              {cardUrl && (
                <div className="mt-4 rounded-2xl overflow-hidden border-2 border-cream" data-testid="share-preview">
                  <img src={cardUrl} alt={en ? 'Preview of the picture to share' : 'Pratonton gambar untuk dikongsi'} className="w-full block" />
                </div>
              )}

              <div className="flex gap-3 mt-4">
                <button data-testid="share-make-btn" onClick={makeCard} disabled={cardBusy}
                  className="flex-1 min-h-[48px] rounded-2xl bg-cream font-heading font-bold text-navy disabled:opacity-50">
                  {cardBusy ? t('shareBusy') : cardUrl ? t('shareAgain') : t('shareMake')}
                </button>
                {cardUrl && (
                  <button data-testid="share-send-btn" onClick={sendCard}
                    className="flex-1 min-h-[48px] rounded-2xl bg-sky font-heading font-bold text-white">
                    {t('shareSend')}
                  </button>
                )}
              </div>
            </div>

            {/* Growth evidence — one honest sentence beats a dashboard. */}
            <div className="bg-white/90 backdrop-blur rounded-3xl p-6 shadow-sm" data-testid="growth-panel">
              <div className="flex items-center gap-2">
                <TrendingUp size={18} strokeWidth={2.6} className="text-leaf" />
                <h2 className="font-heading text-lg font-bold text-navy">{t('growthTitle').replace('{name}', childName)}</h2>
              </div>
              {!growth.enough ? (
                <p className="font-body text-navy/50 text-sm mt-3" data-testid="growth-early">
                  {t('growthEarly').replace('{name}', childName)}
                </p>
              ) : (
                <p className="font-body text-navy text-base mt-3 leading-snug" data-testid="growth-line">
                  {(growth.improved ? t('growthLine') : t('growthSteady'))
                    .replace('{weeks}', growth.weeks)
                    .replace('{before}', growth.before.toFixed(1))
                    .replace('{now}', growth.now.toFixed(1))}
                </p>
              )}
            </div>

            {/* First-time moments — a parent rarely sees the learning happen.
                These are the moments they missed, told as sentences not stats. */}
            <div className="bg-white/90 backdrop-blur rounded-3xl p-6 shadow-sm" data-testid="moments-panel">
              <h2 className="font-heading text-lg font-bold text-navy">{t('momentsTitle')}</h2>
              {milestoneLines.length === 0 ? (
                <p className="font-body text-navy/50 text-sm mt-3" data-testid="moments-empty">
                  {t('momentsEmpty').replace('{name}', childName)}
                </p>
              ) : (
                <ul className="mt-3 space-y-3" data-testid="moments-list">
                  {milestoneLines.map((m) => (
                    <li key={m.key} className="flex gap-3 items-start" data-testid={`moment-${m.key}`}>
                      <Sparkles size={17} strokeWidth={2.6} className="text-sunshine shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="font-body text-sm text-navy leading-snug">{m.text}</p>
                        <p className="font-body text-[11px] text-navy/40 mt-0.5">{m.when}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Strong / practice skills */}
            <div className="bg-white/90 backdrop-blur rounded-3xl p-6 shadow-sm">
              <h2 className="font-heading text-lg font-bold text-navy">{t('skillSummary')}</h2>
              {Object.keys(st.skills).length === 0 ? (
                <p className="font-body text-navy/50 text-sm mt-3">{en ? 'Play a session to see a summary.' : 'Main satu sesi untuk melihat ringkasan.'}</p>
              ) : (
                <>
                  <div className="mt-3" data-testid="skills-strong">
                    <p className="font-body text-xs font-bold text-green-800">{en ? 'Strong skills' : 'Kemahiran kuat'}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {report.strong.length === 0 ? <span className="font-body text-xs text-navy/40">{en ? 'Keep playing to build strengths.' : 'Terus bermain untuk membina kekuatan.'}</span>
                        : report.strong.map((k) => <span key={k} className="bg-leaf/20 text-green-800 font-body text-xs font-bold px-3 py-1.5 rounded-full">{SKILL_LABELS[k]?.[en ? 1 : 0] || k}</span>)}
                    </div>
                  </div>
                  <div className="mt-4" data-testid="skills-practice">
                    <p className="font-body text-xs font-bold text-coral">{en ? 'Needs more practice' : 'Perlu latihan lagi'}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {report.practice.length === 0 ? <span className="font-body text-xs text-navy/40">{en ? 'Nothing flagged right now.' : 'Tiada yang perlu perhatian buat masa ini.'}</span>
                        : report.practice.map((k) => <span key={k} className="bg-coral/15 text-coral font-body text-xs font-bold px-3 py-1.5 rounded-full">{SKILL_LABELS[k]?.[en ? 1 : 0] || k}</span>)}
                    </div>
                  </div>
                  <div className="mt-4 border-t border-navy/5 pt-1">
                    {Object.entries(st.skills).map(([k, v]) => <SkillRow key={k} skill={k} data={v} en={en} />)}
                  </div>
                </>
              )}
            </div>

            {/* Trend 7/30 days */}
            <div className="bg-white/90 backdrop-blur rounded-3xl p-6 shadow-sm" data-testid="trend-card">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-navy flex items-center gap-2"><TrendingUp size={18} className="text-sky" />Trend</h2>
                <div className="flex gap-1 bg-cream rounded-full p-1">
                  {[7, 30].map((n) => (
                    <button key={n} data-testid={`trend-toggle-${n}`} onClick={() => setTrendRange(n)}
                      className={`px-3 py-1 rounded-full font-body text-xs font-bold ${trendRange === n ? 'bg-navy text-white' : 'text-navy/50'}`}>{n} {en ? 'days' : 'hari'}</button>
                  ))}
                </div>
              </div>
              {(() => {
                const data = trendRange === 7 ? report.trend7 : report.trend30;
                const max = Math.max(1, ...data.map((d) => d.stars));
                const rounds = data.reduce((a, d) => a + d.rounds, 0);
                const ft = data.reduce((a, d) => a + d.firstTry, 0);
                return (
                  <>
                    <div className="flex items-end gap-[3px] h-28 mt-4" data-testid="trend-chart">
                      {data.map((d, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                          <div className="w-full rounded-t-md bg-sunshine transition-all" style={{ height: `${(d.stars / max) * 100}%`, minHeight: d.stars ? 4 : 1, opacity: d.stars ? 1 : 0.15 }} title={`${d.label}: ${d.stars}â­`} />
                          {(trendRange === 7 || i % 5 === 0) && <span className="font-body text-[8px] text-navy/40 whitespace-nowrap">{d.label}</span>}
                        </div>
                      ))}
                    </div>
                    <p className="font-body text-xs text-navy/55 mt-3">
                      {en ? `â­ ${data.reduce((a, d) => a + d.stars, 0)} stars · ${rounds} rounds` : `â­ ${data.reduce((a, d) => a + d.stars, 0)} bintang · ${rounds} pusingan`}
                      {rounds > 0 && ` · ${en ? 'accuracy' : 'ketepatan'} ${Math.round((ft / rounds) * 100)}%`}
                    </p>
                  </>
                );
              })()}
            </div>

            {/* Difficulty + recommendation */}
            <div className="bg-white/90 backdrop-blur rounded-3xl p-6 shadow-sm md:col-span-2">
              <h2 className="font-heading text-lg font-bold text-navy">{en ? 'Learning level & next step' : 'Tahap pembelajaran & langkah seterusnya'}</h2>
              {Object.keys(skillLevels).length === 0 ? (
                <p className="font-body text-navy/50 text-sm mt-3">{en ? 'Difficulty adapts automatically as your child plays.' : 'Kesukaran menyesuai secara automatik apabila anak bermain.'}</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-2 mt-3">
                  {Object.entries(skillLevels).map(([sk, lvl]) => (
                    <div key={sk} className="flex items-center justify-between bg-cream rounded-xl px-4 py-2.5" data-testid={`difficulty-${sk}`}>
                      <span className="font-body text-sm text-navy/80">{levelPhrase(sk, lvl)}</span>
                      <span className="flex gap-1">{[1, 2, 3].map((l) => <span key={l} className={`w-2.5 h-2.5 rounded-full ${l <= lvl ? 'bg-sunshine' : 'bg-navy/10'}`} />)}</span>
                    </div>
                  ))}
                </div>
              )}
              {recommended && (
                <p className="font-body text-sm text-navy/70 mt-4" data-testid="recommended-next">
                  {en ? 'Recommended next game: ' : 'Cadangan game seterusnya: '}<b>{gameName(recommended, en)}</b>
                  {report.practice.includes(recommended.skill) && <span className="text-navy/45"> — {en ? 'supports' : 'menyokong'} {(SKILL_LABELS[recommended.skill]?.[en ? 1 : 0] || '').toLowerCase()}</span>}
                </p>
              )}
              <p className="font-body text-[11px] text-navy/35 mt-3">{en ? 'No IQ scores, no diagnosis, no comparison with other children.' : 'Tiada skor IQ, tiada diagnosis, tiada perbandingan dengan kanak-kanak lain.'}</p>
            </div>

            {/* Recent sessions */}
            <div className="bg-white/90 backdrop-blur rounded-3xl p-6 shadow-sm">
              <h2 className="font-heading text-lg font-bold text-navy">{t('recentSessions')}</h2>
              {st.sessions.length === 0 ? (
                <p className="font-body text-navy/50 text-sm mt-3">{en ? 'No sessions yet.' : 'Belum ada sesi.'}</p>
              ) : st.sessions.slice(0, isPro ? 10 : 3).map((s, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-navy/5 last:border-0" data-testid={`session-row-${i}`}>
                  <div>
                    <p className="font-body font-semibold text-navy capitalize">{gameName(GAMES.find((g) => g.id === s.game) || { name: [t(s.world), t(s.world)] }, en)}</p>
                    <p className="font-body text-xs text-navy/50">{new Date(s.date).toLocaleDateString()} · {en ? 'Level' : 'Tahap'} {s.level || 1} · {s.activities.length} {en ? 'rounds' : 'pusingan'}{s.activities.some((a) => a.hints > 0) ? (en ? ' · with a little help' : ' · dengan sedikit bantuan') : ''}</p>
                  </div>
                  <span className="flex items-center gap-1 font-heading font-bold text-navy"><Star size={16} className="text-sunshine fill-sunshine" />{s.stars}</span>
                </div>
              ))}
            </div>

            {/* Home missions */}
            <div className="bg-white/90 backdrop-blur rounded-3xl p-6 shadow-sm">
              <h2 className="font-heading text-lg font-bold text-navy">{t('suggested')}</h2>
              <ul className="mt-3 space-y-2">
                {suggested.map((m) => (
                  <li key={m.id} className="font-body text-navy/75 flex gap-2"><span className="text-leaf">â—</span>{en ? m.en : m.ms}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {tab === 'settings' && (
          <div className="grid md:grid-cols-2 gap-5 mt-6">
            <div className="bg-white/90 rounded-3xl p-6 shadow-sm space-y-4">
              <h2 className="font-heading text-lg font-bold text-navy">{t('language')}</h2>
              <div className="flex gap-2">
                {[['ms', 'BM'], ['en', 'EN']].map(([v, l]) => (
                  <button key={v} data-testid={`setting-lang-${v}`} onClick={() => updProfile({ language: v })}
                    className={`px-5 py-2.5 rounded-full font-body font-bold min-h-[44px] ${st.profile.language === v ? 'bg-sunshine text-navy' : 'bg-cream text-navy/50'}`}>{l}</button>
                ))}
                <button data-testid="setting-bilingual" onClick={() => { if (!isPro && !st.profile.bilingual) { toast.info(en ? 'Full bilingual mode is part of Pro.' : 'Mod dwibahasa penuh adalah sebahagian Pro.'); return; } updProfile({ bilingual: !st.profile.bilingual }); }}
                  className={`px-5 py-2.5 rounded-full font-body font-bold min-h-[44px] ${st.profile.bilingual ? 'bg-leaf text-navy' : 'bg-cream text-navy/50'}`}>
                  {t('bilingualMode')} {st.profile.bilingual ? '✓' : ''}</button>
              </div>
              <h2 className="font-heading text-lg font-bold text-navy pt-2">Audio</h2>
              {[['voice', t('voiceLbl')], ['music', t('musicLbl')], ['sfx', t('sfxLbl')]].map(([k, l]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="font-body text-navy">{l}</span>
                  <button data-testid={`setting-${k}-toggle`} onClick={() => updSettings({ [k]: !st.settings[k] })}
                    className={`w-14 h-8 rounded-full transition-colors relative ${st.settings[k] ? 'bg-leaf' : 'bg-navy/15'}`}>
                    <span className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-all ${st.settings[k] ? 'left-7' : 'left-1'}`} />
                  </button>
                </div>
              ))}
            </div>
            <div className="bg-white/90 rounded-3xl p-6 shadow-sm space-y-4">
              <h2 className="font-heading text-lg font-bold text-navy">{t('sessionLen')}</h2>
              <div className="flex gap-2">
                {[8, 10, 12].map((m) => (
                  <button key={m} data-testid={`setting-minutes-${m}`} onClick={() => updSettings({ sessionMinutes: m })}
                    className={`px-5 py-2.5 rounded-full font-body font-bold min-h-[44px] ${st.settings.sessionMinutes === m ? 'bg-sunshine text-navy' : 'bg-cream text-navy/50'}`}>{m} {t('minutes')}</button>
                ))}
              </div>
              <div className="flex items-center justify-between pt-2">
                <span className="font-body text-navy">{t('missionsLbl')}</span>
                <button data-testid="setting-missions-toggle" onClick={() => updSettings({ missions: !st.settings.missions })}
                  className={`w-14 h-8 rounded-full relative ${st.settings.missions ? 'bg-leaf' : 'bg-navy/15'}`}>
                  <span className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-all ${st.settings.missions ? 'left-7' : 'left-1'}`} />
                </button>
              </div>
              <h2 className="font-heading text-lg font-bold text-coral pt-3">{en ? 'Danger zone' : 'Zon berhati-hati'}</h2>
              {!confirmReset ? (
                <button data-testid="reset-progress-btn" onClick={() => setConfirmReset(true)} className="font-body font-bold text-navy bg-cream rounded-full px-5 py-2.5 min-h-[44px]">{t('resetProgress')}</button>
              ) : (
                <div className="flex gap-2">
                  <button data-testid="reset-confirm-btn" onClick={() => { setSt(resetProgress()); setConfirmReset(false); toast.success('OK'); }} className="font-body font-bold text-white bg-coral rounded-full px-5 py-2.5 min-h-[44px]">{en ? 'Confirm reset' : 'Sahkan set semula'}</button>
                  <button onClick={() => setConfirmReset(false)} className="font-body text-navy/60 min-h-[44px]">{t('cancel')}</button>
                </div>
              )}
              {!confirmDelete ? (
                <button data-testid="delete-profile-btn" onClick={() => setConfirmDelete(true)} className="font-body font-bold text-coral bg-coral/10 rounded-full px-5 py-2.5 min-h-[44px] block">{t('deleteProfile')}</button>
              ) : (
                <div className="flex gap-2">
                  <button data-testid="delete-confirm-btn" onClick={() => { deleteProfile(); nav('/'); }} className="font-body font-bold text-white bg-coral rounded-full px-5 py-2.5 min-h-[44px]">{en ? 'Confirm delete' : 'Sahkan padam'}</button>
                  <button onClick={() => setConfirmDelete(false)} className="font-body text-navy/60 min-h-[44px]">{t('cancel')}</button>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'upgrade' && (
          <div className="mt-6 space-y-5">
            {!user && (
              <div className="bg-white/90 rounded-3xl p-6 shadow-sm max-w-md" data-testid="parent-auth-card">
                <h2 className="font-heading text-lg font-bold text-navy">
                  {authMode === 'forgot' ? (en ? 'Reset password' : 'Set semula kata laluan') : (en ? 'Parent account' : 'Akaun ibu bapa')}
                </h2>
                {authMode !== 'forgot' ? (
                  <>
                    <p className="font-body text-sm text-navy/60 mt-1">{en ? 'Needed only for Pro subscription. Playing stays free without an account.' : 'Diperlukan hanya untuk langganan Pro. Bermain kekal percuma tanpa akaun.'}</p>
                    <input data-testid="auth-phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                      placeholder={en ? 'Phone number (e.g. 0123456789)' : 'No. telefon (cth: 0123456789)'}
                      className="mt-4 w-full h-12 rounded-xl border-2 border-peach bg-cream px-4 font-body text-navy outline-none" />
                    <input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={en ? 'Password (min 6)' : 'Kata laluan (min 6)'}
                      className="mt-3 w-full h-12 rounded-xl border-2 border-peach bg-cream px-4 font-body text-navy outline-none" />
                    {authMode === 'register' && (
                      <label className="mt-3 flex items-start gap-3 min-h-[44px] font-body text-xs text-navy/70">
                        <input data-testid="register-whatsapp-consent" type="checkbox"
                          checked={registerWaConsent} onChange={(e) => setRegisterWaConsent(e.target.checked)}
                          className="mt-1 w-6 h-6 shrink-0" />
                        <span>{t('weeklyWaConsent')}</span>
                      </label>
                    )}
                    <button data-testid="auth-submit-btn" disabled={busy} onClick={doAuth}
                      className="mt-4 w-full h-12 rounded-xl bg-navy text-white font-body font-bold disabled:opacity-50">
                      {authMode === 'login' ? (en ? 'Log in' : 'Log Masuk') : (en ? 'Create account' : 'Cipta Akaun')}</button>
                    <button data-testid="auth-switch-btn" onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}
                      className="mt-2 w-full font-body text-sm text-navy/60 underline min-h-[40px]">
                      {authMode === 'login' ? (en ? 'New? Create account' : 'Baru? Cipta akaun') : (en ? 'Have an account? Log in' : 'Ada akaun? Log masuk')}</button>
                    {authMode === 'login' && (
                      <button data-testid="auth-forgot-btn" onClick={() => { setAuthMode('forgot'); setOtpSent(false); }}
                        className="w-full font-body text-sm text-coral underline min-h-[40px]">{en ? 'Forgot password?' : 'Lupa kata laluan?'}</button>
                    )}
                  </>
                ) : (
                  <>
                    <p className="font-body text-sm text-navy/60 mt-1">
                      {otpSent
                        ? (en ? 'Enter the OTP code we sent to your WhatsApp, then set a new password.' : 'Masukkan kod OTP yang dihantar ke WhatsApp anda, kemudian set kata laluan baharu.')
                        : (en ? "We'll send an OTP code to your registered number via WhatsApp." : 'Kami akan hantar kod OTP ke nombor berdaftar anda melalui WhatsApp.')}
                    </p>
                    <input data-testid="forgot-phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={otpSent}
                      placeholder={en ? 'Registered phone number' : 'No. telefon berdaftar'}
                      className="mt-4 w-full h-12 rounded-xl border-2 border-peach bg-cream px-4 font-body text-navy outline-none disabled:opacity-60" />
                    {!otpSent ? (
                      <button data-testid="send-otp-btn" disabled={busy || !phone} onClick={sendOtp}
                        className="mt-4 w-full h-12 rounded-xl bg-navy text-white font-body font-bold disabled:opacity-50">
                        {busy ? (en ? 'Sending…' : 'Menghantar…') : (en ? 'Send OTP via WhatsApp' : 'Hantar OTP melalui WhatsApp')}</button>
                    ) : (
                      <>
                        <input data-testid="otp-input" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                          placeholder={en ? '6-digit OTP code' : 'Kod OTP 6 digit'}
                          className="mt-3 w-full h-12 rounded-xl border-2 border-peach bg-cream px-4 font-body text-navy outline-none tracking-[0.3em] text-center font-bold" />
                        <input data-testid="new-password-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                          placeholder={en ? 'New password (min 6)' : 'Kata laluan baharu (min 6)'}
                          className="mt-3 w-full h-12 rounded-xl border-2 border-peach bg-cream px-4 font-body text-navy outline-none" />
                        <button data-testid="reset-password-btn" disabled={busy || otp.length !== 6 || newPassword.length < 6} onClick={resetPw}
                          className="mt-4 w-full h-12 rounded-xl bg-navy text-white font-body font-bold disabled:opacity-50">
                          {en ? 'Set new password' : 'Set kata laluan baharu'}</button>
                        <button data-testid="resend-otp-btn" disabled={busy} onClick={sendOtp}
                          className="mt-2 w-full font-body text-sm text-navy/60 underline min-h-[40px]">{en ? 'Resend OTP' : 'Hantar semula OTP'}</button>
                      </>
                    )}
                    <button data-testid="forgot-back-btn" onClick={() => { setAuthMode('login'); setOtpSent(false); setOtp(''); }}
                      className="mt-1 w-full font-body text-sm text-navy/60 underline min-h-[40px]">{en ? 'Back to login' : 'Kembali ke log masuk'}</button>
                  </>
                )}
              </div>
            )}

            {user && isPro ? (
              <div className="bg-white/90 rounded-3xl p-6 shadow-sm" data-testid="manage-pro-card">
                <div className="flex items-center gap-2"><Crown className="text-sunshine" /><h2 className="font-heading text-xl font-bold text-navy">{t('managePro')}</h2></div>
                <div className="grid sm:grid-cols-2 gap-3 mt-4 font-body text-sm text-navy/80">
                  <p>{en ? 'Plan' : 'Pelan'}: <b>PintarPlay Pro</b></p>
                  <p>{en ? 'Provider' : 'Penyedia'}: <b>{entitlement.provider === 'STRIPE' ? 'Kad / Apple Pay' : 'FPX (Billplz)'}</b></p>
                  <p>{en ? 'Renewal' : 'Pembaharuan'}: <b>{
                    entitlement.renewalType === 'LIFETIME' ? (en ? 'None — bought outright' : 'Tiada — dibeli terus')
                      : entitlement.renewalType === 'AUTOMATIC' ? (en ? 'Automatic monthly' : 'Automatik bulanan')
                        : (en ? 'Manual (30 days)' : 'Manual (30 hari)')}</b></p>
                  {/* A lifetime expiry is 50 years out. Printing that date reads
                      as an arbitrary deadline, which is the opposite of what
                      was bought. */}
                  <p>{en ? 'Access until' : 'Akses sehingga'}: <b data-testid="access-expires">{
                    entitlement.renewalType === 'LIFETIME' ? (en ? 'Forever' : 'Selamanya')
                      : entitlement.accessExpiresAt ? new Date(entitlement.accessExpiresAt).toLocaleDateString() : '-'}</b></p>
                  {entitlement.cancelAtPeriodEnd && <p className="text-coral">{en ? 'Auto-renewal cancelled — access continues until the date above.' : 'Pembaharuan automatik dibatalkan — akses kekal sehingga tarikh di atas.'}</p>}
                </div>
                <div className="flex flex-wrap gap-3 mt-5">
                  {/* Nothing recurs on a lifetime purchase, so there is nothing
                      to cancel and nothing to renew. Showing either button
                      would imply an ongoing charge that does not exist. */}
                  {entitlement.renewalType === 'LIFETIME' ? (
                    <p className="font-body text-sm font-bold text-navy/70" data-testid="lifetime-note">
                      {en ? 'Bought once. Nothing renews, nothing to cancel.' : 'Dibeli sekali. Tiada pembaharuan, tiada apa nak dibatalkan.'}
                    </p>
                  ) : (<>
                  {entitlement.provider === 'STRIPE' && !entitlement.cancelAtPeriodEnd && (
                    <button data-testid="cancel-renewal-btn" disabled={busy} onClick={cancelStripe} className="font-body font-bold text-coral bg-coral/10 rounded-full px-5 py-2.5 min-h-[44px]">{en ? 'Cancel auto-renewal' : 'Batal pembaharuan automatik'}</button>
                  )}
                  {entitlement.provider === 'BILLPLZ' && (
                    <button data-testid="renew-fpx-btn" disabled={busy} onClick={payFpx} className="font-body font-bold text-navy bg-sunshine rounded-full px-5 py-2.5 min-h-[44px]">{en ? 'Renew with FPX — RM14.50' : 'Perbaharui dengan FPX — RM14.50'}</button>
                  )}
                  </>)}
                  <button data-testid="recheck-btn" onClick={() => { refresh(); toast.info('OK'); }} className="font-body text-navy/60 underline min-h-[44px]">{en ? 'Recheck status' : 'Semak semula status'}</button>
                </div>
                {transactions.length > 0 && (
                  <div className="mt-5">
                    <h3 className="font-heading font-bold text-navy text-sm">{en ? 'Payment history' : 'Sejarah pembayaran'}</h3>
                    {transactions.map((tx, i) => (
                      <p key={i} className="font-body text-xs text-navy/60 mt-1">RM{(tx.amountMinor / 100).toFixed(2)} · {tx.provider} · {tx.paidAt ? new Date(tx.paidAt).toLocaleDateString() : ''}</p>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white/90 rounded-3xl p-7 shadow-sm" data-testid="upgrade-card">
                <div className="flex items-center gap-2"><Crown className="text-sunshine" size={28} /><h2 className="font-heading text-2xl font-bold text-navy">PintarPlay Pro</h2></div>
                <p className="font-heading text-3xl font-bold text-navy mt-2" data-testid="pro-price">{t('price')}</p>
                {report.completed > 0 && (() => {
                  const skillEntries = Object.entries(st.skills || {});
                  const topSkill = skillEntries.length ? skillEntries.slice().sort((a, b) => (b[1].attempts || 0) - (a[1].attempts || 0))[0][0] : null;
                  const proCount = proGameCount();
                  return (
                    <div className="mt-4 bg-cream/70 rounded-2xl p-4" data-testid="upgrade-personal-block">
                      <div className="grid grid-cols-3 gap-2">
                        <div className="bg-white rounded-xl px-2 py-2.5 text-center" data-testid="upgrade-stat-completed">
                          <p className="font-heading text-xl font-bold text-navy">{report.completed}</p>
                          <p className="font-body text-[10px] font-bold text-navy/50">{t('upgGames')}</p>
                        </div>
                        <div className="bg-white rounded-xl px-2 py-2.5 text-center" data-testid="upgrade-stat-skill">
                          <p className="font-heading text-sm font-bold text-navy leading-tight mt-1.5">{topSkill ? (SKILL_LABELS[topSkill]?.[en ? 1 : 0] || topSkill) : '—'}</p>
                          <p className="font-body text-[10px] font-bold text-navy/50 mt-1">{t('upgSkill')}</p>
                        </div>
                        <div className="bg-white rounded-xl px-2 py-2.5 text-center" data-testid="upgrade-stat-stars">
                          <p className="font-heading text-xl font-bold text-navy">{st.progress.starsEarned ?? st.progress.stars}</p>
                          <p className="font-body text-[10px] font-bold text-navy/50">{t('upgStars')}</p>
                        </div>
                      </div>
                      <p className="font-body text-sm font-bold text-navy/80 mt-3" data-testid="upgrade-personal-line">
                        {t('upgradePersonal')
                          .replace('{name}', st.profile.nickname || (en ? 'Your child' : 'Anak anda'))
                          .replace('{n}', report.completed)
                          .replace('{m}', proCount)}
                      </p>
                    </div>
                  );
                })()}
                <ul className="font-body text-navy/75 mt-4 space-y-1.5 text-sm">
                  <li>✓ {en
                    ? `${proWorldsWithGames().length} more learning worlds — ${proGameCount()} games ready today`
                    : `${proWorldsWithGames().length} dunia pembelajaran lagi — ${proGameCount()} permainan sedia hari ini`}</li>
                  <li>✓ {en ? 'Weekly progress report via WhatsApp' : 'Laporan progress mingguan melalui WhatsApp'}</li>
                  <li>✓ {en ? '30+ home & movement missions' : '30+ misi rumah & pergerakan'}</li>
                  <li>✓ {en ? 'Full bilingual Malay–English mode' : 'Mod dwibahasa Melayu–Inggeris penuh'}</li>
                  <li>✓ {en ? 'Adaptive difficulty & full parent reports' : 'Kesukaran adaptif & laporan penuh ibu bapa'}</li>
                  <li>✓ {en ? 'Extended Piko rewards' : 'Ganjaran Piko tambahan'}</li>
                  <li>✓ {en ? 'Cancel anytime (auto-renewing methods)' : 'Batal bila-bila masa (kaedah auto-baharu)'}</li>
                </ul>
                <div className="grid sm:grid-cols-2 gap-4 mt-6">
                  <div className="border-2 border-navy/10 rounded-2xl p-5">
                    <h3 className="font-heading font-bold text-navy">{en ? 'Card / Apple Pay' : 'Kad / Apple Pay'}</h3>
                    <p className="font-body text-xs text-navy/60 mt-1">{en ? 'RM14.50 charged automatically every month until cancelled. Apple Pay appears at checkout on supported devices.' : 'RM14.50 dicaj secara automatik setiap bulan sehingga dibatalkan. Apple Pay dipaparkan semasa pembayaran pada peranti yang menyokong.'}</p>
                    <button data-testid="pay-stripe-btn" disabled={busy} onClick={() => payStripe('monthly')}
                      className="mt-3 w-full h-12 rounded-xl bg-navy text-white font-body font-bold disabled:opacity-40">{t('subscribe')}</button>
                  </div>
                  <div className="border-2 border-navy/10 rounded-2xl p-5">
                    <h3 className="font-heading font-bold text-navy">{en ? 'Online Banking (FPX)' : 'Perbankan Dalam Talian (FPX)'}</h3>
                    <p className="font-body text-xs text-navy/60 mt-1">{en ? 'Pay RM14.50 now for 30 days of Pro access. Renew manually when the period ends. Not auto-renewing.' : 'Bayar RM14.50 sekarang untuk 30 hari akses Pro. Perbaharui secara manual apabila tempoh tamat. Bukan auto-baharu.'}</p>
                    <button data-testid="pay-fpx-btn" disabled={busy} onClick={() => payFpx('monthly')}
                      className="mt-3 w-full h-12 rounded-xl bg-sky text-white font-body font-bold disabled:opacity-40">{en ? 'Pay with FPX' : 'Bayar dengan FPX'}</button>
                  </div>
                </div>
                {/* One-time purchase. Sits below the two recurring options and
                    is deliberately framed against them — the comparison to
                    "about two and a half months" is what makes it legible. */}
                <div className="rounded-2xl p-5 mt-4" style={{ border: '2px solid #FFD166', background: 'rgba(255,209,102,0.12)' }}>
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <h3 className="font-heading font-bold text-navy">{en ? 'Buy once — keep forever' : 'Beli sekali — milik selamanya'}</h3>
                    <span className="font-heading text-2xl font-bold text-navy">RM37</span>
                  </div>
                  <p className="font-body text-xs text-navy/65 mt-1">
                    {en
                      ? 'One payment of RM37. Pro stays unlocked forever — no renewal, nothing to cancel, no recurring charge. Costs about the same as two and a half months of the monthly plan.'
                      : 'Satu bayaran RM37. Pro terbuka selamanya — tiada pembaharuan, tiada apa nak dibatalkan, tiada caj berulang. Lebih kurang harga dua setengah bulan pelan bulanan.'}
                  </p>
                  <button data-testid="pay-lifetime-btn" disabled={busy} onClick={() => payFpx('lifetime')}
                    className="mt-3 w-full h-12 rounded-xl bg-sunshine text-navy font-body font-bold disabled:opacity-40">
                    {en ? 'Buy lifetime access — RM37' : 'Beli akses selamanya — RM37'}
                  </button>
                </div>
                {!user && (
                  <p className="font-body text-xs text-navy/55 mt-3" data-testid="pay-no-account-note">
                    {en
                      ? 'Subscribing needs an account. Playing stays free without one.'
                      : 'Melanggan perlukan akaun. Main tetap percuma tanpa akaun.'}
                  </p>
                )}
                <p className="font-body text-[11px] text-navy/40 mt-4">{en ? 'No hidden fees. No child-facing purchases. Progress is never deleted when a subscription ends — only Pro content locks.' : 'Tiada bayaran tersembunyi. Tiada pembelian dalam mod kanak-kanak. Kemajuan tidak akan dipadam apabila langganan tamat — hanya kandungan Pro dikunci.'}</p>
              </div>
            )}
            {user && (
              <button data-testid="logout-btn" onClick={logout} className="font-body text-navy/50 underline min-h-[44px]">{t('logout')} ({user.phone})</button>
            )}
          </div>
        )}

        {tab === 'affiliate' && user && isPro && (
          <ParentAffiliatePanel
            user={user}
            isPro={isPro}
            onEntitlementLost={() => setTab('upgrade')}
          />
        )}

        {/* Free, or not signed in: show the programme, locked. Same lock
            pattern as the weekly WhatsApp report so the panel reads as one
            product. The numbers are an EXAMPLE and say so — never a real
            wallet, and never a promise of earnings. */}
        {tab === 'affiliate' && !(user && isPro) && (
          <div className="bg-white/90 backdrop-blur rounded-3xl p-6 shadow-sm mt-6" data-testid="affiliate-locked-card">
            <div className="flex items-center gap-2">
              <TrendingUp size={18} className="text-leaf" />
              <h2 className="font-heading text-lg font-bold text-navy">{t('affiliate')}</h2>
              <span className="ml-auto font-body text-[11px] font-bold text-navy/60 bg-cream rounded-full px-3 py-1">Pro</span>
            </div>
            <p className="font-body text-navy/70 text-sm mt-2">
              {en
                ? 'Share PintarPlay with other parents. Pro parents earn 10% of every eligible MYR payment, after the holding period.'
                : 'Kongsi PintarPlay dengan ibu bapa lain. Ibu bapa Pro menerima 10% bagi setiap bayaran MYR yang layak, selepas tempoh menunggu.'}
            </p>
            <div className="relative mt-4">
              <div className="rounded-2xl p-4" style={{ background: '#FFF6E0', border: '1px solid rgba(230,184,76,0.28)' }}>
                <p className="font-body text-[11px] font-bold uppercase tracking-wider text-navy/45">
                  {en ? 'Your referral link' : 'Pautan rujukan anda'}
                </p>
                <p className="font-body text-sm text-navy/80 mt-1 break-all">https://pintarplay.com/?ref=••••••••</p>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  {[
                    [en ? 'Total commission' : 'Jumlah komisen', 'RM0.00'],
                    [en ? 'Available' : 'Boleh dikeluarkan', 'RM0.00'],
                  ].map(([k, v]) => (
                    <div key={k} className="rounded-2xl bg-white/80 px-4 py-3">
                      <p className="font-body text-[11px] text-navy/45">{k}</p>
                      <p className="font-heading text-xl font-bold text-navy">{v}</p>
                    </div>
                  ))}
                </div>
                <p className="font-body text-[11px] text-navy/45 mt-3">
                  {en ? 'Example only — not a real balance or a promise of earnings.'
                    : 'Contoh sahaja — bukan baki sebenar atau janji pendapatan.'}
                </p>
              </div>
              <div className="absolute inset-0 rounded-2xl flex flex-col items-center justify-center gap-3 backdrop-blur-[3px]"
                style={{ background: 'rgba(252,249,242,0.55)' }} data-testid="affiliate-lock-overlay">
                <div className="w-12 h-12 rounded-full bg-navy flex items-center justify-center"><Lock size={20} className="text-sunshine" /></div>
                <p className="font-body text-sm font-bold text-navy text-center px-6">
                  {en ? 'The affiliate programme is for Pro parents.'
                    : 'Program affiliate adalah untuk ibu bapa Pro.'}
                </p>
                <button data-testid="affiliate-upgrade-btn" onClick={() => setTab('upgrade')}
                  className="font-body font-bold text-navy bg-sunshine rounded-full px-6 py-2.5 min-h-[44px] shadow">
                  {en ? 'Upgrade to Pro' : 'Naik taraf ke Pro'}
                </button>
              </div>
            </div>
          </div>
        )}

        {SHOW_VOICE_REVIEW && tab === 'voice' && <VoiceReview en={en} />}

        {tab === 'about' && (
          <div className="grid md:grid-cols-2 gap-5 mt-6">
            <div className="bg-white/90 rounded-3xl p-6 shadow-sm">
              <div className="flex items-center gap-2"><Shield className="text-leaf" /><h2 className="font-heading text-lg font-bold text-navy">{t('privacy')}</h2></div>
              <ul className="font-body text-sm text-navy/75 mt-3 space-y-2 list-disc pl-5">
                <li>{en ? "Your child's progress is stored only in this browser (local-first). It is not uploaded." : 'Kemajuan anak disimpan hanya dalam pelayar ini (local-first). Ia tidak dimuat naik.'}</li>
                <li>{en ? 'A parent account (phone number only) is created solely for Pro subscriptions and WhatsApp reports.' : 'Akaun ibu bapa (nombor telefon sahaja) dicipta hanya untuk langganan Pro dan laporan WhatsApp.'}</li>
                <li>{en ? 'No ads, no behavioural analytics, no tracking pixels, no camera/microphone/location.' : 'Tiada iklan, tiada analitik tingkah laku, tiada penjejakan, tiada kamera/mikrofon/lokasi.'}</li>
                <li>{en ? 'If browser data is cleared manually, local progress will be lost.' : 'Jika data pelayar dipadam secara manual, kemajuan setempat akan hilang.'}</li>
                <li>{en ? 'You can reset progress or delete the profile at any time in Settings.' : 'Anda boleh set semula kemajuan atau padam profil pada bila-bila masa dalam Tetapan.'}</li>
              </ul>
            </div>
            <div className="bg-white/90 rounded-3xl p-6 shadow-sm">
              <h2 className="font-heading text-lg font-bold text-navy">{t('about')}</h2>
              <p className="font-body text-sm text-navy/75 mt-3 leading-relaxed">
                {en ? 'PintarPlay is a learning-through-play app for children aged 2–3 combining short interactive games, spoken instructions, music, movement, and real-world activities with parents. It supports early language, memory, attention and problem-solving practice.'
                  : 'PintarPlay ialah aplikasi belajar sambil bermain untuk kanak-kanak 2–3 tahun yang menggabungkan permainan interaktif pendek, arahan bersuara, muzik, pergerakan, dan aktiviti dunia sebenar bersama ibu bapa. Ia menyokong latihan bahasa awal, ingatan, tumpuan dan penyelesaian masalah asas.'}
              </p>
              <p className="font-body text-sm text-navy/60 mt-3">
                {en ? 'PintarPlay does not measure IQ, does not diagnose development, and does not replace teachers or doctors.' : 'PintarPlay tidak mengukur IQ, tidak mendiagnosis perkembangan, dan tidak menggantikan guru atau doktor.'}
              </p>
              <p className="font-body text-xs text-navy/40 mt-4">{t('version')}: 1.0.0 · {en ? 'Support' : 'Sokongan'}: support@pintarplay.app</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
